/**
 * WS envelope types for IA-EVA Full Conversation Agent v5.
 */

/** Wire identifier expuesto en el envelope `protocol`. */
export type WsProtocolVersion = 'fc-v5';

/** Lista canónica de protocolos que el FE soporta. */
export const SUPPORTED_PROTOCOLS: readonly WsProtocolVersion[] = ['fc-v5'] as const;

/**
 * Envelope endurecido v5 — campos comunes a TODO evento BE → FE.
 *
 * Notas:
 *   - `provider_epoch` puede venir cuando el backend rota el voice client.
 *   - `requires_ack` true → el FE debe responder `client_ack` (ver §5 del FE_CHANGES).
 *   - `attempt_id`, `question_idx`, `question_id` son opcionales (existen en eventos
 *     vinculados a una pregunta concreta).
 */
export interface WsOutboundEnvelope {
  event_id: string;
  server_seq: number;
  sent_at: string; // ISO8601
  protocol: WsProtocolVersion;
  requires_ack: boolean;
  attempt_id?: string;
  provider_epoch: number;
  question_idx?: number;
  question_id?: string;
}

/**
 * Lista de eventos críticos que requieren `client_ack` en fc-v5.
 *
 * El BE marca `requires_ack: true` en estos, pero el FE también puede validarlos
 * por type para defensa en profundidad.
 */
export const CRITICAL_ACK_TYPES_V5: readonly string[] = [
  'session_ack',
  'assistant_turn_started',
  'next_question',
  'assistant_logical_complete',
  'mic_open',
  'user_turn_complete',
  'followup_requested',
  'interview_complete',
  'session_expired',
  'capture_stalled',
  'state_resync',
] as const;

/**
 * Snapshot mínimo del estado del FE incluido en el `client_ack`.
 *
 * Snapshot mínimo del estado del FE incluido en el `client_ack` v5.
 */
export interface ClientAckPayload {
  type: 'client_ack';
  protocol: WsProtocolVersion;
  event_id: string | undefined;
  server_seq: number | undefined;
  applied: boolean;
  client_phase: string | undefined;
  local_audio_playing: boolean;
  last_eva_audio_seq: number;
  last_client_audio_seq: number;
}

/**
 * Entrada cacheada del AckCache — guarda el payload wire (idempotente por
 * event_id) más un timestamp local de debug. `debugTs` NO se serializa al
 * WebSocket — mantener estricto para evitar drift de contrato.
 */
export interface AckCacheEntry {
  payload: ClientAckPayload;
  debugTs: number;
}

/**
 * Type guard liviano para detectar envelopes v5 a partir del payload
 * parseado. NO valida shape completo — solo el discriminador `protocol`.
 */
export function isV5Envelope(payload: unknown): payload is WsOutboundEnvelope {
  if (!payload || typeof payload !== 'object') return false;
  const proto = (payload as { protocol?: unknown }).protocol;
  return proto === 'fc-v5';
}
